/**
 * Canonical user-role taxonomy — the SINGLE source for these values.
 * `@stopal/db` builds the `user_role` Postgres enum FROM this array
 * (`pgEnum('user_role', USER_ROLES)`), so the two can't drift and no lockstep
 * test is needed. This leaf package is the shared home because the frontend can
 * import it but can't import `@stopal/db`.
 *
 * Role gates the SURFACE a user can enter; privileges gate what they can do
 * inside `/ops`. See `ROLE_SURFACES` on the web side for the surface map.
 */
export const USER_ROLES = [
  'customer',
  // A Stopal Business customer. A distinct kind of user from a consumer, but
  // still a customer — provisioned by ops, never self-assigned (the `role`
  // additionalField is `input: false`, so the account holder cannot write it).
  'business',
  'driver',
  'employee',
  'superadmin',
] as const

export type UserRole = (typeof USER_ROLES)[number]

/**
 * The platform-superadmin role string.
 *
 * Here rather than beside the enum in `@stopal/db`, because this leaf is the
 * lowest layer every consumer can import — the frontend cannot reach
 * `@stopal/db` at all, so a copy of the literal grew there instead. `satisfies
 * UserRole` is what keeps it honest: rename the value in the array above and
 * this line stops compiling rather than silently naming a role that does not
 * exist.
 */
export const SUPERADMIN_ROLE = 'superadmin' as const satisfies UserRole

/**
 * The role whose JOB is driving. Distinct from the driver SURFACE, which a
 * superadmin also reaches — `SURFACE_ROLES.driver` answers "may enter", this
 * answers "is one". Ops screens that mark the difference need the second
 * question, and spelling it as a literal is what the class-closing scan in
 * `driver-surface-authz.test.ts` exists to catch.
 */
export const DRIVER_ROLE = 'driver' as const satisfies UserRole

/**
 * Roles whose activity is real customer traffic.
 *
 * Kept as a SET rather than a single role so that adding a customer-kind role
 * is a decision someone has to make here, rather than a silent default. The
 * previous single-value form meant a new customer role was treated as internal
 * staff and dropped from analytics with no error and no signal.
 */
export const CUSTOMER_ROLES: readonly UserRole[] = ['customer', 'business']

export function isCustomerRole(role: string | null | undefined): boolean {
  return role != null && (CUSTOMER_ROLES as readonly string[]).includes(role)
}
